import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Zap, Building2, Crown, Loader2, AlertTriangle } from 'lucide-react';
import { useSiteAuth } from '../../context/SiteAuthContext';
import { useToast } from '../../context/ToastContext';
import sitesApi from '../../services/sitesApi';

export default function SiteSubscription() {
    const navigate = useNavigate();
    const { siteUser } = useSiteAuth();
    const toast = useToast();

    const [subDetails, setSubDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(true);
    const [subscribing, setSubscribing] = useState(null);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelImmediate, setCancelImmediate] = useState(false);
    const [showTransitionModal, setShowTransitionModal] = useState(false);
    const [selectedPlanName, setSelectedPlanName] = useState(null);
    const [transitionImmediate, setTransitionImmediate] = useState(true);

    useEffect(() => {
        const fetchDetails = async () => {
            try {
                const res = await sitesApi.getSubscriptionDetails();
                setSubDetails(res.data);
            } catch (err) {
                console.error('Failed to load subscription details', err);
            } finally {
                setLoadingDetails(false);
            }
        };
        fetchDetails();
    }, []);

    const handleSubscribe = async (planName, isImmediate = true) => {
        if (planName === 'Free') {
            setShowCancelModal(true);
            return;
        }

        setSubscribing(planName);
        try {
            const res = await sitesApi.post('/api/sites/subscription/subscribe', {
                plan_name: planName,
                billing_cycle: 'monthly',
                transition_immediate: isImmediate
            });

            if (res.data.status === 'scheduled') {
                toast.success(`Plan change to ${planName} successfully scheduled for the next cycle!`);
                window.location.reload();
                return;
            }

            const { subscription_id, key_id } = res.data;

            // Load Razorpay checkout script
            const rzpScript = document.createElement('script');
            rzpScript.src = 'https://checkout.razorpay.com/v1/checkout.js';
            rzpScript.async = true;
            document.body.appendChild(rzpScript);

            rzpScript.onload = () => {
                const options = {
                    key: key_id,
                    subscription_id: subscription_id,
                    name: 'Aaharam Sites',
                    description: `Authorize Subscription mandate - ${planName} Plan`,
                    config: {
                        display: {
                            blocks: {
                                cardBlock: {
                                    name: 'Pay using Card',
                                    instruments: [{ method: 'card' }]
                                }
                            },
                            sequence: ['block.cardBlock'],
                            preferences: {
                                show_default_blocks: false
                            }
                        }
                    },
                    handler: async function (response) {
                        setSubscribing(planName);
                        try {
                            await sitesApi.post('/api/sites/subscription/verify', {
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_subscription_id: response.razorpay_subscription_id,
                                razorpay_signature: response.razorpay_signature,
                                plan_name: planName
                            });
                            toast.success('Subscription activated successfully!');
                            window.location.reload();
                        } catch (verifyErr) {
                            console.error('Verification error:', verifyErr);
                            toast.error(verifyErr.response?.data?.error || 'Failed to verify subscription activation.');
                        } finally {
                            setSubscribing(null);
                        }
                    },
                    prefill: {
                        name: siteUser.name || 'Static User',
                        email: siteUser.email || 'static@gmail.com',
                        contact: siteUser.phone || '9999999999'
                    },
                    theme: {
                        color: '#f97316'
                    }
                };
                const razorpay = new window.Razorpay(options);
                razorpay.open();
            };

            rzpScript.onerror = () => {
                toast.error('Failed to load Razorpay SDK. Please check your network connection.');
            };

        } catch (err) {
            console.error('Checkout error:', err);
            toast.error(err.response?.data?.error || 'Failed to initiate checkout.');
        } finally {
            setSubscribing(null);
        }
    };

    const handleConfirmDowngrade = async () => {
        setSubscribing('Free');
        try {
            const res = await sitesApi.post('/api/sites/subscription/cancel', {
                cancel_immediate: cancelImmediate
            });
            toast.success(res.data.message || 'Subscription cancelled successfully.');
            setShowCancelModal(false);
            window.location.reload();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.error || 'Failed to cancel subscription.');
        } finally {
            setSubscribing(null);
        }
    };

    const getPlanIndex = (planId) => {
        if (planId === 'Free') return 0;
        if (planId === 'Basic') return 1;
        if (planId === 'Pro') return 2;
        return 0;
    };

    const currentPlan = subDetails?.plan_name || siteUser?.plan_name || 'Free';
    const isTrial = subDetails?.subscription_status === 'trialing';

    const getRemainingTrialDays = () => {
        if (!subDetails?.trial_end) return null;
        const end = new Date(subDetails.trial_end);
        const now = new Date();
        const diff = end - now;
        const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
        return days > 0 ? days : 0;
    };

    const trialDaysLeft = getRemainingTrialDays();

    const plans = [
        {
            id: 'Free',
            name: 'Free',
            icon: <Building2 className="text-slate-400" size={32} />,
            price: '₹0',
            period: '/forever',
            description: 'Perfect for getting started and trying out the builder.',
            features: [
                '1 Website (Subdomain only)',
                'Basic Templates',
                'Aaharam Branding',
                'Community Support',
                'No Custom Domains'
            ]
        },
        {
            id: 'Basic',
            name: 'Basic',
            icon: <Zap className="text-primary-500" size={32} />,
            price: '₹250',
            period: '/month',
            description: 'Great for small restaurants looking for a professional presence.',
            features: [
                'Up to 5 Websites',
                'All Templates',
                'No Branding',
                'Email Support',
                '1 Custom Domain Publish',
                'Basic Analytics'
            ]
        },
        {
            id: 'Pro',
            name: 'Pro',
            icon: <Crown className="text-amber-500" size={32} />,
            price: '₹799',
            period: '/month',
            description: 'For growing restaurant businesses and franchises.',
            features: [
                'Unlimited Websites',
                'Premium Templates',
                'No Branding',
                'Priority 24/7 Support',
                'Unlimited Custom Domains',
                'Advanced Analytics',
                'E-commerce Integration'
            ],
            popular: true
        }
    ];

    return (
        <div className="min-h-screen bg-slate-950 text-slate-300 pb-20">
            <div className="bg-slate-900 border-b border-slate-800 py-6">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors">
                        <ArrowLeft size={20} />
                        Back to Dashboard
                    </button>
                    <div className="text-center max-w-2xl mx-auto">
                        <h1 className="text-4xl font-extrabold text-white mb-4">Simple, transparent pricing</h1>
                        <p className="text-xl text-slate-400">Choose the perfect plan for your restaurant business. All plans include our drag-and-drop builder.</p>
                    </div>
                </div>
            </div>

            {/* Scheduled Plan Change Banner */}
            {subDetails?.scheduled_plan && (
                <div className="max-w-3xl mx-auto px-4 mt-8 animate-fade-in">
                    <div className="bg-gradient-to-r from-indigo-950/60 to-slate-900/80 border border-indigo-500/40 rounded-2xl p-6 flex items-start gap-4 shadow-xl">
                        <div className="w-12 h-12 bg-indigo-500/10 text-indigo-400 rounded-xl flex items-center justify-center border border-indigo-500/20 shrink-0">
                            <Zap size={22} className="animate-pulse" />
                        </div>
                        <div>
                            <h4 className="text-base font-bold text-white mb-1">Plan Switch Scheduled</h4>
                            <p className="text-slate-300 text-sm leading-relaxed">
                                Your current subscription remains active. Your plan will automatically transition to <strong className="text-white font-extrabold">{subDetails.scheduled_plan}</strong> at the end of your billing cycle on <strong className="text-white font-extrabold">{subDetails.current_end ? new Date(subDetails.current_end).toLocaleDateString(undefined, { dateStyle: 'long' }) : 'renewal date'}</strong>. No immediate charges will be made.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Trial Banner */}
            {isTrial && trialDaysLeft !== null && (
                <div className="max-w-3xl mx-auto px-4 mt-8">
                    <div className="bg-gradient-to-r from-primary-900/50 to-indigo-900/50 border border-primary-500/30 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between text-center sm:text-left gap-4 shadow-xl">
                        <div>
                            <h3 className="text-lg font-bold text-white mb-1">You are on the Free Trial</h3>
                            <p className="text-slate-300 text-sm">Experience all premium features before the trial ends.</p>
                        </div>
                        <div className="shrink-0">
                            <div className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-bold shadow-lg">
                                {trialDaysLeft} Days Left
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
                {loadingDetails ? (
                    <div className="flex justify-center py-20">
                        <Loader2 className="animate-spin text-primary-500" size={40} />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto items-stretch">
                        {plans.map((plan) => {
                            const isCurrent = currentPlan === plan.id;
                            const isScheduled = subDetails?.scheduled_plan === plan.id;
                            const currentIndex = getPlanIndex(currentPlan);
                            const targetIndex = getPlanIndex(plan.id);
                            
                            let buttonText = 'Select Plan';
                            let buttonClass = 'bg-slate-800 hover:bg-slate-700 text-white';
                            let isBtnDisabled = isCurrent || isScheduled || subscribing !== null;
                            
                            if (isCurrent) {
                                buttonText = 'Current Plan';
                                buttonClass = 'bg-slate-800 text-slate-500 border border-slate-700 cursor-default';
                            } else if (isScheduled) {
                                buttonText = 'Scheduled for Next Cycle';
                                buttonClass = 'bg-indigo-950 text-indigo-400 border border-indigo-900/50 cursor-default';
                            } else if (targetIndex > currentIndex) {
                                buttonText = `Upgrade to ${plan.name}`;
                                buttonClass = plan.id === 'Pro' ? 'bg-primary-600 hover:bg-primary-700 text-white' : 'bg-slate-800 hover:bg-slate-700 text-white';
                            } else {
                                buttonText = `Downgrade to ${plan.name}`;
                                buttonClass = 'bg-slate-850 hover:bg-slate-800 text-slate-300 border border-slate-850';
                            }

                            return (
                                <div 
                                    key={plan.id} 
                                    className={`relative bg-slate-900 rounded-3xl border ${
                                        isCurrent 
                                            ? 'border-indigo-500 shadow-2xl shadow-indigo-950/40 ring-1 ring-indigo-500/30' 
                                            : isScheduled
                                                ? 'border-indigo-500/50 shadow-2xl shadow-indigo-950/20'
                                                : plan.popular 
                                                    ? 'border-primary-500 shadow-2xl shadow-primary-900/10' 
                                                    : 'border-slate-800'
                                    } p-8 flex flex-col`}
                                >
                                    {isCurrent && (
                                        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-indigo-600 text-white px-4 py-0.5 rounded-full text-[10px] font-black shadow-lg tracking-widest uppercase">
                                            Current Plan
                                        </div>
                                    )}
                                    {isScheduled && (
                                        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-indigo-500 text-white px-4 py-0.5 rounded-full text-[10px] font-black shadow-lg tracking-widest uppercase">
                                            Scheduled
                                        </div>
                                    )}
                                    {!isCurrent && !isScheduled && plan.popular && (
                                        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-primary-600 text-white px-4 py-0.5 rounded-full text-[10px] font-black shadow-lg tracking-widest uppercase">
                                            Recommended
                                        </div>
                                    )}
                                    
                                    <div className="mb-6">
                                        {plan.icon}
                                    </div>
                                    <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                                    <p className="text-slate-400 text-sm mb-6 h-10">{plan.description}</p>
                                    <div className="mb-8">
                                        <span className="text-4xl font-extrabold text-white">{plan.price}</span>
                                        <span className="text-slate-500 font-medium">{plan.period}</span>
                                    </div>
                                    
                                    <ul className="space-y-4 mb-8 flex-1">
                                        {plan.features.map((feature, idx) => (
                                            <li key={idx} className="flex items-start gap-3">
                                                <Check size={20} className="text-primary-500 shrink-0" />
                                                <span className="text-slate-300 text-sm">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    
                                    <button
                                        onClick={() => {
                                            if (plan.id === 'Free') {
                                                setShowCancelModal(true);
                                            } else if (currentPlan !== 'Free') {
                                                setSelectedPlanName(plan.id);
                                                setShowTransitionModal(true);
                                            } else {
                                                handleSubscribe(plan.id, true);
                                            }
                                        }}
                                        disabled={isBtnDisabled}
                                        className={`w-full py-3 rounded-xl font-bold transition-colors flex items-center justify-center gap-2 ${buttonClass}`}
                                    >
                                        {subscribing === plan.id && <Loader2 className="animate-spin" size={16} />}
                                        {subscribing === plan.id ? 'Connecting...' : buttonText}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            {/* Cancel Confirmation Modal */}
            {showCancelModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 w-full max-w-md shadow-2xl text-center animate-scale-in">
                        <div className="w-16 h-16 bg-rose-500/10 text-rose-500 rounded-2xl flex items-center justify-center mx-auto border border-rose-500/20 mb-6">
                            <AlertTriangle className="w-8 h-8" />
                        </div>
                        
                        <h3 className="text-xl font-bold text-white mb-3">Cancel Subscription?</h3>
                        <p className="text-slate-400 text-sm leading-relaxed mb-6">
                            How would you like to handle your cancellation?
                        </p>
                        
                        <div className="space-y-3 mb-8 text-left">
                            <label className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer transition-all ${!cancelImmediate ? 'bg-indigo-950/20 border-indigo-500/50' : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'}`}>
                                <input 
                                    type="radio" 
                                    name="cancel_option" 
                                    checked={!cancelImmediate} 
                                    onChange={() => setCancelImmediate(false)} 
                                    className="mt-1 accent-indigo-500" 
                                />
                                <div>
                                    <span className="block text-sm font-bold text-white">End of Billing Cycle</span>
                                    <span className="block text-xs text-slate-400 mt-0.5">Keep premium features active until your cycle ends. You will not be billed again.</span>
                                </div>
                            </label>
                            
                            <label className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer transition-all ${cancelImmediate ? 'bg-rose-950/10 border-rose-500/30' : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'}`}>
                                <input 
                                    type="radio" 
                                    name="cancel_option" 
                                    checked={cancelImmediate} 
                                    onChange={() => setCancelImmediate(true)} 
                                    className="mt-1 accent-rose-500" 
                                />
                                <div>
                                    <span className="block text-sm font-bold text-white">Cancel Immediately</span>
                                    <span className="block text-xs text-slate-400 mt-0.5">End subscription right now and immediately lose access to premium templates & domains.</span>
                                </div>
                            </label>
                        </div>
                        
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setShowCancelModal(false)}
                                className="flex-1 px-4 py-3 rounded-xl text-slate-300 hover:bg-slate-800 transition-colors border border-slate-800 text-sm font-medium"
                            >
                                Keep Plan
                            </button>
                            <button
                                onClick={handleConfirmDowngrade}
                                disabled={subscribing !== null}
                                className="flex-1 px-4 py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold transition-colors disabled:opacity-50 text-sm"
                            >
                                {subscribing === 'Free' ? 'Downgrading...' : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Plan Transition Modal */}
            {showTransitionModal && selectedPlanName && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
                    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 w-full max-w-md shadow-2xl text-center animate-scale-in">
                        <div className="w-16 h-16 bg-indigo-500/10 text-indigo-400 rounded-2xl flex items-center justify-center mx-auto border border-indigo-500/20 mb-6">
                            <Zap className="w-8 h-8 animate-pulse" />
                        </div>
                        
                        <h3 className="text-xl font-bold text-white mb-3">Apply Plan Change</h3>
                        <p className="text-slate-400 text-sm leading-relaxed mb-6">
                            How would you like to apply your change to the <span className="text-white font-bold">{selectedPlanName}</span> plan?
                        </p>
                        
                        <div className="space-y-3 mb-8 text-left">
                            <label className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer transition-all ${transitionImmediate ? 'bg-indigo-950/20 border-indigo-500/50' : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'}`}>
                                <input 
                                    type="radio" 
                                    name="transition_option" 
                                    checked={transitionImmediate} 
                                    onChange={() => setTransitionImmediate(true)} 
                                    className="mt-1 accent-indigo-500" 
                                />
                                <div>
                                    <span className="block text-sm font-bold text-white">Apply Immediately</span>
                                    <span className="block text-xs text-slate-400 mt-0.5">Switch immediately. You will authorize a new mandate and the change takes effect today.</span>
                                </div>
                            </label>
                            
                            <label className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer transition-all ${!transitionImmediate ? 'bg-indigo-950/20 border-indigo-500/50' : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'}`}>
                                <input 
                                    type="radio" 
                                    name="transition_option" 
                                    checked={!transitionImmediate} 
                                    onChange={() => setTransitionImmediate(false)} 
                                    className="mt-1 accent-indigo-500" 
                                />
                                <div>
                                    <span className="block text-sm font-bold text-white">Schedule for Next Cycle</span>
                                    <span className="block text-xs text-slate-400 mt-0.5">Keep current features. Plan and renewal charges switch to the new rate at renewal automatically.</span>
                                </div>
                            </label>
                        </div>
                        
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setShowTransitionModal(false)}
                                className="flex-1 px-4 py-3 rounded-xl text-slate-300 hover:bg-slate-800 transition-colors border border-slate-800 text-sm font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    setShowTransitionModal(false);
                                    handleSubscribe(selectedPlanName, transitionImmediate);
                                }}
                                disabled={subscribing !== null}
                                className="flex-1 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-colors disabled:opacity-50 text-sm"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
